import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Shield, AlertCircle, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import heroImage from "@/assets/blog-police-rights.jpg";
import { generateArticleSchema } from "@/lib/seo-schemas";

const PoliceRights = () => {
  const schema = generateArticleSchema(
    "Your Rights During a Police Stop",
    "Know your constitutional rights during traffic stops and police encounters to protect yourself.",
    "2024-12-20",
    "2024-12-20",
    "https://danielalbertlaw.com/blog/rights-during-police-stop"
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="Your Rights During a Police Stop | Daniel Albert Law"
        description="Know your constitutional rights during traffic stops and police encounters. Expert criminal defense attorney guidance on protecting your rights."
        keywords="police rights, traffic stop rights, criminal defense, Houston criminal attorney, constitutional rights"
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: "Your Rights During a Police Stop" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                Criminal Defense
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>December 20, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
              Your Rights During a Police Stop
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Understanding your constitutional rights during police encounters is crucial for every driver and citizen. Whether it's a routine traffic stop or a more serious investigation, knowing what you can and cannot do—and what the police can and cannot do—can make a significant difference in the outcome of the encounter and protect you from potential legal issues.
              </p>
            </div>

            {/* Quick Reference Card */}
            <Card className="mb-12 bg-gradient-subtle">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Shield className="h-6 w-6 text-accent" />
                  Your Constitutional Rights
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Scale className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>4th Amendment:</strong> Protection against unreasonable searches and seizures</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Scale className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>5th Amendment:</strong> Right to remain silent and not incriminate yourself</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Scale className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span><strong>6th Amendment:</strong> Right to have an attorney present during questioning</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-bold mb-6">During a Traffic Stop: What to Do</h2>
                
                <div className="space-y-6">
                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">1</span>
                      Pull Over Safely
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Turn on your signal, slow down, and pull over to a safe location as quickly as possible. Turn off your engine, turn on your interior light (if at night), and keep your hands visible on the steering wheel.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">2</span>
                      Stay Calm and Be Polite
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Remain calm, speak respectfully, and avoid making sudden movements. Do not argue, become belligerent, or physically resist, even if you believe the stop is unjustified. You can challenge the stop later in court.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">3</span>
                      Provide Required Documents
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      In Texas, you are required to show your driver's license, proof of insurance, and vehicle registration when asked during a traffic stop. Inform the officer before reaching for these documents.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">4</span>
                      Limit Your Responses
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      You must provide your name if asked, but beyond that, you have the right to remain silent. You can politely decline to answer questions about where you're going, where you've been, or what you've been doing. A simple "I'd prefer not to answer that question" is sufficient.
                    </p>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3 flex items-center gap-2">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold">5</span>
                      Do Not Consent to Searches
                    </h3>
                    <p className="text-lg text-muted-foreground">
                      Police need either your consent, a warrant, or probable cause to search your vehicle. You have the right to refuse consent to a search. Clearly state: "I do not consent to any searches." If they proceed anyway, do not physically resist, but make your objection clear.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Can Police Search Your Vehicle?</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Police can search your vehicle without a warrant in several situations:
                </p>
                <ul className="space-y-4 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>You Give Consent:</strong> If you agree to let them search, they can. Never consent to a search unless you're absolutely certain there's nothing illegal in your vehicle.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Probable Cause:</strong> If officers have probable cause to believe there's contraband or evidence of a crime in your vehicle (for example, they smell marijuana), they can search without your consent.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Incident to Arrest:</strong> If you're arrested, police can search your vehicle and any containers within your immediate reach.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Plain View:</strong> If illegal items are in plain view, officers can seize them and potentially search further.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <strong>Inventory Search:</strong> If your vehicle is impounded, police can conduct an inventory search of its contents.
                    </div>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Your Right to Remain Silent</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  The 5th Amendment protects you from self-incrimination. Here's how to exercise this right:
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">What to Say</h3>
                    <p className="text-muted-foreground">
                      "Officer, I'm exercising my right to remain silent" or "I don't wish to answer any questions without my attorney present." Be clear and direct.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">When to Use It</h3>
                    <p className="text-muted-foreground">
                      You can invoke your right to silence at any time during a police encounter. Once you do, officers should stop questioning you. If they continue, remain silent and document the violation.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">Important Note</h3>
                    <p className="text-muted-foreground">
                      In Texas, you must verbally invoke your right to remain silent. Simply staying quiet is not enough. You must explicitly state that you're exercising this right.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">What About Passengers?</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Passengers have rights too:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>In Texas, passengers generally don't have to show ID unless they're suspected of a crime</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Passengers can ask if they're free to leave; if yes, they can calmly exit the vehicle and walk away</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Passengers have the same right to remain silent as drivers</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Police can order passengers to get out of the vehicle for safety reasons</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">What NOT to Do During a Police Stop</h2>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't argue with officers or become confrontational</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't resist arrest, even if you believe it's unlawful</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't lie to officers—it's better to remain silent than to lie</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't give consent to search your vehicle</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't reach for anything without informing the officer first</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-1" />
                    <span>Don't admit to anything—even seemingly minor admissions can be used against you</span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">After the Stop: What to Do Next</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  If you were arrested or cited during the stop:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Write down everything you remember about the stop as soon as possible</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Note the officer's name, badge number, and patrol car number</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Take photos of the scene if possible</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Get contact information from any witnesses</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Contact a criminal defense attorney immediately—do not discuss your case with anyone else</span>
                  </li>
                </ul>
              </section>
            </div>

            {/* Related Services */}
            <Card className="mt-12 bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Were Your Rights Violated?</h2>
                <p className="text-lg mb-6 opacity-95">
                  If you believe your rights were violated during a police stop or if you're facing criminal charges, contact Daniel Albert Law Firm immediately. Our experienced criminal defense attorneys will review your case and fight to protect your rights.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/practice-areas/criminal-defense">
                      Learn More About Criminal Defense
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <CTASection
        title="Facing Criminal Charges?"
        description="Don't face the criminal justice system alone. Contact us today for experienced, aggressive defense representation."
      />

      <Footer />
    </div>
  );
};

export default PoliceRights;